'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';

import { Logo } from '@/components/logo';
import { Button } from '@/components/ui/button';
import { useMobileMenu } from '@/hooks/use-mobile-menu';
import { trackCtaClicked } from '@/lib/posthog';

const navigationLinks = [
  { href: '/features', label: 'Features' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/blog', label: 'Blog' },
  { href: '/templates', label: 'Templates' },
  { href: '/guides', label: 'Guides' },
] as const;

/**
 * Marketing navigation component for public-facing pages.
 *
 * Features:
 * - Responsive mobile menu with body scroll lock
 * - Hydration-safe state management
 * - Accessible keyboard navigation (Escape to close)
 * - Auto-closes on navigation
 *
 * @note Uses suppressHydrationWarning for mobile menu state to prevent
 * hydration mismatches between server and client rendering.
 */
export function MarketingNav() {
  const { isOpen: mobileOpen, toggle: toggleMobileNav, close: closeMobileNav } = useMobileMenu();
  const pathname = usePathname();

  // Normalize the pathname into a page name used in analytics events so funnels
  // can group by origin page (e.g. 'landing', 'pricing', 'features').
  const trackingPage =
    pathname === '/' ? 'landing' : pathname.replace(/^\//, '').replace(/\//g, '_') || 'landing';

  const trackNavCtaClick = () =>
    trackCtaClicked({
      location: 'nav',
      destination: '/login',
      page: trackingPage,
      label: 'Get Started',
    });

  // Handle Escape key to close menu
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileOpen) {
        closeMobileNav();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileOpen, closeMobileNav]);

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-white/[0.08] bg-black/35 backdrop-blur-xl supports-[backdrop-filter]:bg-black/25">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <div className="container mx-auto flex items-center justify-between px-4 py-3">
        <Logo size="sm" />

        <div className="hidden md:flex items-center gap-6 text-sm font-medium">
          {navigationLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              aria-current={pathname === link.href ? 'page' : undefined}
              className={[
                'relative text-sm transition-colors',
                pathname === link.href
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {link.label}
              <span
                className={[
                  'pointer-events-none absolute -bottom-2 left-0 right-0 h-px',
                  pathname === link.href
                    ? 'bg-gradient-to-r from-transparent via-white/70 to-transparent'
                    : 'bg-transparent',
                ].join(' ')}
              />
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center">
          <Button
            asChild
            size="sm"
            className="bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700"
          >
            <Link href="/login" onClick={trackNavCtaClick}>
              Get Started
            </Link>
          </Button>
        </div>

        <button
          type="button"
          aria-expanded={mobileOpen}
          aria-label="Toggle navigation menu"
          onClick={toggleMobileNav}
          className="md:hidden inline-flex items-center justify-center rounded-md bg-muted/30 p-2 transition hover:bg-muted/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          suppressHydrationWarning
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={closeMobileNav}
          />
          <div
            className="md:hidden absolute left-0 right-0 top-full z-50 border-t border-white/[0.08] bg-black/70 backdrop-blur-xl"
            suppressHydrationWarning
          >
            <div className="container mx-auto flex flex-col gap-2 px-4 py-4 text-base font-medium">
              {navigationLinks.map((link) => {
                const isActive = pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={[
                      'rounded-xl px-3 py-3 transition',
                      isActive
                        ? 'bg-muted/50 text-foreground border border-border'
                        : 'text-muted-foreground hover:bg-muted/30',
                    ].join(' ')}
                    onClick={closeMobileNav}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="pt-2">
                <Button
                  asChild
                  size="lg"
                  className="w-full justify-center bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700"
                >
                  <Link
                    href="/login"
                    onClick={() => {
                      trackNavCtaClick();
                      closeMobileNav();
                    }}
                  >
                    Get Started
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}
